import React, { useEffect, useRef, useState } from 'react';
import { FormikValues } from 'formik';
import styled from 'styled-components';

import { Card, FormHelperText, Grid, IconButton } from '@material-ui/core';
import {
  Assignment as AssignmentIcon,
  Info as InfoIcon
} from '@material-ui/icons';
import { InlineForm, InlineFormik } from '@sierralabs/react-material-ui-formik';

import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineSelect from '../../../common/inline/InlineSelect';
import InlineTextField from '../../../common/inline/InlineTextField';
import CardTitleContent from '../../../common/card/CardTitleContent';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateHazardCause } from '../Hazard.hooks';
import { HazardCardInfoDialog } from '../dialogs/HazardCardInfoDialog';
import {
  CauseProbabilityTable,
  HazardCause,
  HazardProbability,
  hazardProbability
} from '../Hazard.interface';
import { getRiskLabel } from '../Hazard.helpers';

const StyledCard = styled(Card)`
  .form-control {
    width: 150px;
  }
`;

const HazardCauseResidualAssessmentCard: React.FC<{
  hazardId: number;
  cause: HazardCause;
  onChange?: (cause: HazardCause) => void;
}> = ({ hazardId, cause, onChange }) => {
  const { residualHarm, hazard } = cause;
  const [showInfo, setShowInfo] = useState(false);
  const {
    updatedHazardCause,
    isUpdatingHazardCause,
    updateHazardCauseError,
    updateHazardCause
  } = useUpdateHazardCause(hazardId, cause.id);
  const formikRef = useRef<FormikValues>();
  const onChangeRef = useRef(onChange);
  const { isFieldDisabled } = useTaskEntityFields(cause.task);
  useEffect(
    () =>
      void (updatedHazardCause && onChangeRef.current?.(updatedHazardCause)),
    [updatedHazardCause]
  );
  return (
    <StyledCard>
      <CardTitleContent>
        <CardTitle>
          <AssignmentIcon />
          Residual Likelihood of Harm and Risk
        </CardTitle>
        <IconButton size='small' onClick={() => setShowInfo(true)}>
          <InfoIcon />
        </IconButton>
      </CardTitleContent>
      <CardContent padding='large'>
        <InlineFormik
          error={updateHazardCauseError}
          isSubmitting={isUpdatingHazardCause}
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={{
            residualHarm: residualHarm || ('' as any as HazardProbability),
            residualRiskLabel: getRiskLabel(hazard.residualRpn)
          }}
          onSubmit={(dto: Partial<HazardCause>) => updateHazardCause(dto)}
        >
          <InlineForm>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={6}>
                <FormHelperText>Residual likelihood of harm</FormHelperText>
                <InlineSelect
                  name='residualHarm'
                  label=''
                  disabled={isFieldDisabled('residualHarm')}
                  options={hazardProbability.map(s => ({
                    value: s.value,
                    label: `${s.value} - ${s.label}`
                  }))}
                />
              </Grid>
              <Grid item xs={6}>
                <FormHelperText>Residual risk</FormHelperText>
                <InlineTextField disabled name='residualRiskLabel' />
              </Grid>
            </Grid>
          </InlineForm>
        </InlineFormik>
      </CardContent>
      {showInfo && (
        <HazardCardInfoDialog
          title='Cause Probability Table'
          table={CauseProbabilityTable}
          onClose={() => setShowInfo(false)}
        />
      )}
    </StyledCard>
  );
};

export default HazardCauseResidualAssessmentCard;
